import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { makeStyles } from '@material-ui/core/styles'
import {
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core'
import { Autocomplete, IconButton, MenuItem, Select } from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import {
  isEBPolicy,
  isGroupHealthInsurance,
  isHealthPersonalInsurance,
  isMotorInsurance,
  isMotorPersonalInsurance,
} from 'src/utils/commonMethods'
import { COLORS } from 'src/utils/Themes/colors'
import {
  INTERMEDIARY_VALUE,
  PAYMENT_OPTIONS,
  POLICY_HOLDER_TYPE,
  POLICY_TYPE,
  PRODUCT_TYPE,
  RENEWAL_DECISION,
  RENEWAL_MIGRATION,
} from 'src/utils/constants/Renewals'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import { CLIENT_TYPE, NOTIFICATION_TYPES } from 'src/utils/constants/common'
import ConfirmationModal from 'src/components/common/ConfirmationModal'
import documentDownload from 'src/utils/commonMethods/documentDownload'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import { formatDateString } from 'src/utils/commonMethods/formatDate'
import CustomDatePicker from 'src/components/common/custom-components/CustomDatePicker'
import {
  COVERAGES_WARNING,
  MODAL_MESSAGE,
} from 'src/utils/constants/ValidationErrorMessages'
import { showNotificationMessage } from 'src/actions'
import { useDispatch } from 'react-redux'
import InstallmentCalculator from 'src/components/common/RenewPolicy/InstallmentCalculator'

const TrackFormFields = ({
  expiredPolicyData,
  tpaListData,
  insuranceProviderData,
  closeModal,
  isTracking,
  insuranceTypesData,
  trackedDetails,
  clientType,
  trackedDetailsLoading,
  trackedDetailsError,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const fileInputRef = useRef(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [enteredCoverage, setEnteredCoverages] = useState('')
  const [selectedInsuranceProvider, setSelectedInsuranceProvider] = useState({
    id: trackedDetails?.provider?.id ?? null,
    name: trackedDetails?.provider?.name ?? null,
  })
  const [selectedInsuranceType, setSelectedInsuranceType] = useState({
    insuranceType: expiredPolicyData?.insuranceType ?? null,
  })
  const [isConfirmationModalOpen, setIsConfirmationModal] = useState(false)
  const [subTypes, setSubTypes] = useState({
    productSubType: expiredPolicyData?.productSubType ?? [],
  })
  const [fileUploadError, setFileUploadError] = useState('')
  const [customIntermediary, setCustomIntermediary] = useState('')

  const setFormValues = () => {
    if (trackedDetails) {
      prefilledSavedData(trackedDetails)
    }
  }

  const findUrlById = (array, id) => {
    if (!array?.length) {
      return ''
    }
    const matchingObject = array?.find((item) => item?.id === id)
    return matchingObject ? matchingObject?.url : ''
  }

  useEffect(() => {
    setFormValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedDetails])

  useEffect(() => {
    if (selectedInsuranceType?.insuranceType) {
      const selectedType = insuranceTypesData?.find(
        (type) => type?.insuranceType === selectedInsuranceType?.insuranceType
      )
      setSubTypes(selectedType?.subType || [])
    } else {
      setSubTypes([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInsuranceType])

  const prefilledSavedData = (data) => {
    if (data) {
      const tpaId = data?.otherInformation?.tpa || data?.tpa || ''
      const tpaUrl = findUrlById(tpaListData, tpaId)

      setUploadedFiles(
        data?.policyDocument || trackedDetails?.policyDocument || []
      )
      let intermediaryData = ''

      if (data?.intermediary) {
        intermediaryData =
          data?.intermediary !== INTERMEDIARY_VALUE?.TROVITY
            ? INTERMEDIARY_VALUE?.OTHERS
            : data?.intermediary
        if (data?.intermediary !== INTERMEDIARY_VALUE?.TROVITY) {
          setCustomIntermediary(data?.intermediary)
        }
      }
      formik.setValues(
        {
          decision: RENEWAL_DECISION?.VERIFICATION_COMPLETE,
          typeOfPolicy: data?.typeOfPolicy || '',
          intermediary: intermediaryData || '',
          noOfEmployees: data?.totalNoOfEmployees || '',
          coverRange: data?.coverRange || '',
          policyHolderType: data?.policyType || '',
          clientName: data?.clientId || '',
          policyId: data?.id || trackedDetails?.id || '',
          policyNumber: data?.policyNumber || '',
          type: data?.type || '',
          provider: {
            id: data?.provider?.id || '',
            name: data?.provider?.name || '',
          },
          insuranceType: data?.insuranceType || '',
          productSubType: data?.productSubType || '',
          sumInsured: data?.sumAssured?.text || data?.sumAssured || '',
          policyStartDate: formatDateString(data?.issuedOn) || '',
          policyEndDate: formatDateString(data?.expiresOn) || '',
          premiumPaid: data?.premiumAmount?.text || data?.premiumAmount || '',
          claimAmount: data?.claimAmount?.text || data?.claimAmount || '',
          coverages: data?.coverageTypes?.split(',') || [],
          individualCoverages: data?.individualCoverageTypes?.split(',') || [],
          agentComments: data?.agentComments || '',
          tpa: tpaId,
          tpaUrl: tpaUrl,
          empMigration: data?.empMigration || '',
          actionType: '',
          insuredName:
            data?.otherInformation?.insuredName || data?.insuredName || '',
          vehicleNumber:
            data?.otherInformation?.vehicleNumber || data?.vehicleNumber || '',
          vehicleMakeModel:
            data?.otherInformation?.vehicleMakeModel ||
            data?.vehicleMakeModel ||
            '',
          state: data?.otherInformation?.state || data?.state || '',
          city: data?.otherInformation?.city || data?.city || '',
          address: data?.otherInformation?.address || data?.address || '',
          paymentType: data?.paymentType || null,
          installmentDetails: data?.installmentDetails || [],
          vehicleIdv: data?.otherInformation?.vehicleIdv || '',
          ncbPercentage: data?.otherInformation?.ncbPercentage || '',
          seatingCapacity: data?.otherInformation?.seatingCapacity || '',
          fuelType: data?.otherInformation?.fuelType || '',
          cc: data?.otherInformation?.cc || '',
          gvw: data?.otherInformation?.gvw || '',
          policyInceptionPremium: data?.policyInceptionPremium || '',
        },
        false
      )
    }
  }

  const formik = useFormik({
    initialValues: {
      decision: '',
      typeOfPolicy: '',
      intermediary: '',
      noOfEmployees: '',
      coverRange: '',
      policyHolderType: '',
      clientName: '',
      policyId: '',
      policyNumber: '',
      type: '',
      provider: {
        id: '',
        name: '',
      },
      insuranceType: '',
      productSubType: '',
      sumInsured: '',
      policyStartDate: '',
      policyEndDate: '',
      premiumPaid: '',
      claimAmount: '',
      coverages: [],
      individualCoverages: [],
      agentComments: '',
      tpa: '',
      tpaUrl: '',
      empMigration: '',
      actionType: '',
      insuredName: '',
      vehicleNumber: '',
      vehicleMakeModel: '',
      state: '',
      city: '',
      address: '',
      paymentType: '',
      installmentDetails: [],
    },
  })

  if (trackedDetailsLoading)
    return (
      <div>
        <CircularProgress />
      </div>
    )

  if (trackedDetailsError)
    return (
      <>
        <GraphQlErrors error={trackedDetailsError} routeValue={'back'} />
      </>
    )

  const handleFileUpload = (event) => {
    if (event?.target?.files) {
      const file = [event?.target?.files?.[0]]
      setUploadedFiles([...file])
    }
    setFileUploadError('')
  }

  const findAndSetUrlById = (array, id) => {
    const matchingObject = array?.find((item) => item?.id === id)
    if (matchingObject) {
      formik?.setFieldValue('tpaUrl', matchingObject?.url, true)
    } else return null
  }

  const renderTextField = ({
    label,
    name,
    type = 'text',
    required = false,
    select = false,
    options = [],
    startIcon,
    disabled,
  }) => (
    <FormControl fullWidth className={classes.fieldContainer}>
      <div className={classes.formLabel}>
        <span>
          {label} {required && <span className={classes.asterisk}>*</span>}
        </span>
      </div>
      {type === 'date' ? (
        <CustomDatePicker
          name={name}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          helperText={formik?.touched?.[name] && formik?.errors?.[name]}
          error={formik?.touched?.[name] && !!formik?.errors?.[name]}
          disabled={disabled}
          value={formik?.values?.[name]}
          className={`${classes.formField} ${classes.datePicker}`}
        />
      ) : (
        <TextField
          variant='outlined'
          name={name}
          type={type}
          size='small'
          required={required}
          select={select}
          className={classes.formField}
          value={formik?.values?.[name]}
          onChange={formik?.handleChange}
          onBlur={formik?.handleBlur}
          helperText={formik?.touched?.[name] && formik?.errors?.[name]}
          error={formik?.touched?.[name] && !!formik?.errors?.[name]}
          disabled={disabled}
          InputProps={{ startAdornment: startIcon }}
        >
          {select &&
            options?.map((option) => (
              <MenuItem key={option?.value} value={option?.value}>
                {option?.label}
              </MenuItem>
            ))}
        </TextField>
      )}
    </FormControl>
  )

  const closeConfirmationModal = () => {
    setIsConfirmationModal(false)
  }

  const handleDocumentDownload = async (document, documentURL) => {
    if (document) {
      documentDownload(document ? document : [], documentURL)
    }
  }

  const handleCustomIntermediaryChange = (event) => {
    setCustomIntermediary(event?.target?.value)
  }

  const isIntermediaryOthers = () => {
    return formik?.values?.intermediary === INTERMEDIARY_VALUE?.OTHERS
  }

  const handleDeleteCoverage = (formik, index) => {
    const newCoverages = [...formik?.values?.coverages]
    newCoverages?.splice(index, 1)
    formik?.setFieldValue('coverages', newCoverages)
  }

  const handleAddCoverage = (
    formik,
    coverage,
    setEnteredCoverages,
    dispatch
  ) => {
    if (!coverage) {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.WARNING,
          message: COVERAGES_WARNING?.EMPTY,
        })
      )
      return
    }

    if (formik?.values?.coverages?.includes(coverage)) {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.WARNING,
          message: COVERAGES_WARNING?.EXISTS,
        })
      )
      return
    }

    if (formik?.values?.coverages?.length >= 5) {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.WARNING,
          message: COVERAGES_WARNING?.LENGTH,
        })
      )
      return
    }

    formik?.setFieldValue('coverages', [...formik?.values?.coverages, coverage])
    setEnteredCoverages('')
  }

  const handleInstallments = (installmentsData) => {
    formik?.setFieldValue('installmentDetails', installmentsData)
  }

  return (
    <>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          id='confirmationModal'
          message={MODAL_MESSAGE.SUBMIT}
          open={isConfirmationModalOpen}
          onClose={closeConfirmationModal}
        />
      )}
      <div className={classes.formContainer}>
        <form>
          <div className={classes.formTextContainer}>
            <div className={classes.formText}>
              <div>
                {!isTracking && (
                  <div className={classes.headingText}>
                    Please enter the details to digitize the policy
                  </div>
                )}
                <FormControl>
                  <FormLabel className={classes.formLabel}>
                    Decision <span className={classes.asterisk}>*</span>
                    {formik?.touched?.decision && formik?.errors?.decision && (
                      <FormHelperText className={classes.asterisk}>
                        {formik?.errors?.decision}
                      </FormHelperText>
                    )}
                  </FormLabel>
                  <RadioGroup
                    name='decision'
                    value={formik?.values?.decision}
                    onChange={formik.handleChange}
                    row
                  >
                    <FormControlLabel
                      value={RENEWAL_DECISION?.VERIFICATION_COMPLETE}
                      control={<Radio />}
                      disabled={isTracking}
                      label='Verified'
                    />
                    <FormControlLabel
                      value={RENEWAL_DECISION?.VERIFICATION_PENDING}
                      control={<Radio />}
                      disabled={isTracking}
                      label='Verification Incomplete'
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              <div className={classes.renewalFilesUpload}>
                <Button
                  variant='outlined'
                  component='label'
                  className={classes.uploadLogoButton}
                >
                  <FileUploadOutlinedIcon />
                  <span className={classes.inputFileName}>
                    Upload your files here
                  </span>
                  <input
                    id='renewalDocumentUpload'
                    type='file'
                    required
                    onChange={handleFileUpload}
                    className={classes.UploadFile}
                    disabled={isTracking}
                    ref={fileInputRef}
                  />
                </Button>
                <div className={classes.errorText}>{fileUploadError}</div>

                {uploadedFiles?.length > 0 && (
                  <div className={classes.filesContainer}>
                    <div className={classes.uploadedFile}>
                      <div className={classes.uploadedFileName}>
                        {uploadedFiles?.[0]?.originalName
                          ? uploadedFiles?.[0]?.originalName
                          : uploadedFiles?.[0]?.name}
                      </div>
                      <div className={classes.iconButton}>
                        <IconButton
                          className={classes.uploadedDocumentDelete}
                          onClick={() => {
                            setUploadedFiles([])
                          }}
                          disabled={uploadedFiles?.[0]?.originalName}
                          size='small'
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDocumentDownload(uploadedFiles)}
                          size='small'
                          className={classes.fileDownload}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Type of policy',
                name: 'typeOfPolicy',
                required: true,
                select: true,
                disabled: isTracking,
                options: [
                  { value: POLICY_TYPE?.NEW, label: 'New' },
                  { value: POLICY_TYPE?.RENEWAL, label: 'Renewal' },
                  { value: POLICY_TYPE?.ROLL_OVER, label: 'Rollover' },
                ],
              })}
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth className={classes.fieldContainer}>
                <div className={classes.formLabel}>
                  <span>
                    Intermediary&nbsp;
                    <span className={classes.asterisk}>*</span>
                  </span>
                </div>
                <Select
                  name='intermediary'
                  variant='outlined'
                  required
                  select
                  disabled={isTracking}
                  value={formik?.values?.intermediary}
                  onChange={formik?.handleChange}
                  className={classes.selectField}
                  fullWidth
                >
                  <MenuItem value={INTERMEDIARY_VALUE?.TROVITY}>
                    Trovity
                  </MenuItem>
                  <MenuItem value={INTERMEDIARY_VALUE?.OTHERS}>Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isIntermediaryOthers() && (
              <Grid item xs={3}>
                <FormControl fullWidth className={classes.fieldContainer}>
                  <div className={classes.formLabel}>
                    <span>Others</span>
                  </div>
                  <TextField
                    name='customIntermediary'
                    variant='outlined'
                    required
                    value={customIntermediary}
                    onChange={handleCustomIntermediaryChange}
                    className={classes.formField}
                    fullWidth
                    size='small'
                  />
                </FormControl>
              </Grid>
            )}
            {!isMotorPersonalInsurance(expiredPolicyData?.insuranceType) && (
              <>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Total no. of employees',
                    name: 'noOfEmployees',
                    type: 'number',
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Cover range',
                    name: 'coverRange',
                    disabled: isTracking,
                  })}
                </Grid>
              </>
            )}
            <Grid item xs={3}>
              {renderTextField({
                label: 'Policy holder type',
                name: 'policyHolderType',
                required: true,
                select: true,
                options: [
                  { value: POLICY_HOLDER_TYPE?.CORPORATE, label: 'Corporate' },
                  {
                    value: POLICY_HOLDER_TYPE?.INDIVIDUAL,
                    label: 'Individual',
                  },
                ],
                disabled: true,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Client name',
                name: 'clientName',
                required: true,
                disabled: true,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Policy number',
                name: 'policyNumber',
                required: true,
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Product type',
                name: 'type',
                required: true,
                select: true,
                options: [
                  { value: PRODUCT_TYPE?.GENERAL, label: 'General' },
                  { value: PRODUCT_TYPE?.LIFE, label: 'Life' },
                ],
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth className={classes.fieldContainer}>
                <div className={classes.formLabel}>
                  <span>
                    Insurance provider name{' '}
                    <span className={classes.asterisk}>*</span>
                  </span>
                </div>
                <Autocomplete
                  name='provider'
                  options={insuranceProviderData}
                  className={classes.autoCompleteContainer}
                  getOptionLabel={(option) => option?.name}
                  disabled={isTracking}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      size='small'
                      name='provider'
                      required
                      className={classes.coveragesField}
                      onBlur={formik?.handleBlur}
                      helperText={
                        formik?.touched?.provider &&
                        formik?.errors?.provider &&
                        (formik?.errors?.provider?.name ||
                          formik?.errors?.provider?.id)
                      }
                      error={
                        formik?.touched?.provider && !!formik?.errors?.provider
                      }
                      InputProps={{ ...params.InputProps, type: 'search' }}
                    />
                  )}
                  value={selectedInsuranceProvider ?? { id: '', name: '' }}
                  onChange={(event, newValue) => {
                    setSelectedInsuranceProvider(newValue)
                    formik?.setFieldValue(
                      'provider',
                      newValue
                        ? { id: newValue?.id, name: newValue?.name }
                        : { id: '', name: '' }
                    )
                  }}
                  onBlur={formik?.handleBlur}
                  isOptionEqualToValue={
                    (option, value) =>
                      option?.id === value?.id && option?.name === value?.name // Customize the comparison
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth className={classes.fieldContainer}>
                <div className={classes.formLabel}>
                  <span>
                    Insurance Type <span className={classes.asterisk}>*</span>
                  </span>
                </div>
                <Autocomplete
                  options={insuranceTypesData}
                  className={classes.autoCompleteContainer}
                  getOptionLabel={(option) => option?.insuranceType}
                  disabled={isTracking}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      size='small'
                      name='insuranceType'
                      required
                      className={classes.coveragesField}
                      onBlur={formik?.handleBlur}
                      helperText={
                        formik?.touched?.insuranceType &&
                        formik?.errors?.insuranceType
                      }
                      error={
                        formik?.touched?.insuranceType &&
                        !!formik?.errors?.insuranceType
                      }
                      InputProps={{ ...params.InputProps, type: 'search' }}
                    />
                  )}
                  value={selectedInsuranceType}
                  onChange={(event, newValue) => {
                    setSelectedInsuranceType(newValue)
                    formik?.setFieldValue(
                      'insuranceType',
                      newValue ? newValue?.insuranceType : ''
                    )
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.insuranceType === value?.insuranceType
                  }
                />
              </FormControl>
            </Grid>
            {subTypes?.length > 0 && (
              <Grid item xs={3}>
                <FormControl fullWidth className={classes.fieldContainer}>
                  <div className={classes.formLabel}>
                    <span>
                      Sub Type <span className={classes.asterisk}>*</span>
                    </span>
                  </div>
                  <Autocomplete
                    options={subTypes}
                    className={classes.autoCompleteContainer}
                    getOptionLabel={(option) => option}
                    disabled={isTracking}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        size='small'
                        name='productSubType'
                        required
                        className={classes.coveragesField}
                        onBlur={formik?.handleBlur}
                        helperText={
                          formik?.touched?.productSubType &&
                          formik?.errors?.productSubType
                        }
                        error={
                          formik?.touched?.productSubType &&
                          !!formik?.errors?.productSubType
                        }
                        InputProps={{ ...params.InputProps, type: 'search' }}
                      />
                    )}
                    value={
                      subTypes?.find(
                        (sub) => sub === formik?.values?.productSubType
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik?.setFieldValue('productSubType', newValue || '')
                    }}
                    isOptionEqualToValue={(option, value) => option === value}
                  />
                </FormControl>
              </Grid>
            )}
            {isMotorInsurance(formik?.values?.insuranceType) ? (
              <Grid item xs={3}>
                {renderTextField({
                  label: 'IDV',
                  name: 'vehicleIdv',
                  type: 'number',
                  required: false,
                  disabled: isTracking,
                })}
              </Grid>
            ) : (
              <Grid item xs={3}>
                {renderTextField({
                  label: 'Sum insured',
                  name: 'sumInsured',
                  required: true,
                  disabled: isTracking,
                })}
              </Grid>
            )}
            <Grid item xs={3}>
              {renderTextField({
                label: 'Policy start date',
                name: 'policyStartDate',
                required: true,
                type: 'date',
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Policy end date',
                name: 'policyEndDate',
                required: true,
                type: 'date',
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Premium paid',
                name: 'premiumPaid',
                required: true,
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Previous claimed amount',
                name: 'claimAmount',
                required: true,
                disabled: isTracking,
              })}
            </Grid>
            {formik?.values?.decision ===
              RENEWAL_DECISION?.VERIFICATION_PENDING && (
              <Grid item xs={3}>
                {renderTextField({
                  label: 'Agent comments',
                  name: 'agentComments',
                  required: true,
                  disabled: isTracking,
                })}
              </Grid>
            )}
            {isGroupHealthInsurance(expiredPolicyData?.insuranceType) && (
              <>
                <Grid item xs={3}>
                  <FormControl fullWidth className={classes.fieldContainer}>
                    <div className={classes.formLabel}>
                      <span>
                        TPA&nbsp;<span className={classes.asterisk}>*</span>
                      </span>
                    </div>
                    <Select
                      id='tpa'
                      name='tpa'
                      placeholder={'Select TPA'}
                      displayEmpty
                      defaultValue=''
                      disabled={isTracking}
                      className={classes.selectField}
                      onChange={(e) => {
                        formik?.setFieldValue('tpa', e?.target?.value, true)
                        findAndSetUrlById(tpaListData, e?.target?.value)
                      }}
                      value={formik?.values?.tpa}
                    >
                      <MenuItem value={''} className={'default-menu-item'}>
                        Select TPA*
                      </MenuItem>
                      {tpaListData?.map((item) => (
                        <MenuItem key={item?.id} value={item?.id}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'TPA url',
                    name: 'tpaUrl',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
              </>
            )}
            {isMotorInsurance(formik?.values?.insuranceType) && (
              <>
                {' '}
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Insured Name',
                    name: 'insuredName',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Vehicle Number',
                    name: 'vehicleNumber',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Vehicle Make & Model',
                    name: 'vehicleMakeModel',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'NCB(%)',
                    name: 'ncbPercentage',
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Seating Capacity',
                    name: 'seatingCapacity',
                    type: 'number',
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Fuel Type',
                    name: 'fuelType',
                    type: 'text',
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Cubic Capacity (CC)',
                    name: 'cc',
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'GVW',
                    name: 'gvw',
                    type: 'number',
                    disabled: isTracking,
                  })}
                </Grid>
              </>
            )}
            {isHealthPersonalInsurance(expiredPolicyData?.insuranceType) && (
              <>
                <Grid item xs={3}>
                  <FormControl fullWidth className={classes.fieldContainer}>
                    <div className={classes.formLabel}>
                      <span>
                        TPA&nbsp;<span className={classes.asterisk}>*</span>
                      </span>
                    </div>
                    <Select
                      id='tpa'
                      name='tpa'
                      placeholder={'Select TPA'}
                      displayEmpty
                      defaultValue=''
                      disabled={isTracking}
                      className={classes.selectField}
                      onChange={(e) => {
                        formik?.setFieldValue('tpa', e?.target?.value, true)
                        findAndSetUrlById(tpaListData, e?.target?.value)
                      }}
                      value={formik?.values?.tpa}
                    >
                      <MenuItem value={''} className={'default-menu-item'}>
                        Select TPA*
                      </MenuItem>
                      {tpaListData?.map((item) => (
                        <MenuItem key={item?.id} value={item?.id}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'TPA url',
                    name: 'tpaUrl',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
              </>
            )}
            <Grid item xs={3}>
              {renderTextField({
                label: 'State',
                name: 'state',
                disabled: isTracking,
              })}
            </Grid>
            {formik?.values?.state && (
              <Grid item xs={3}>
                {renderTextField({
                  label: 'City',
                  name: 'city',
                  disabled: isTracking,
                })}
              </Grid>
            )}
            <Grid item xs={3}>
              {renderTextField({
                label: 'Address',
                name: 'address',
                disabled: isTracking,
              })}
            </Grid>
            <Grid item xs={3}>
              {renderTextField({
                label: 'Mode of payment',
                name: 'paymentType',
                required: true,
                select: true,
                options: [
                  { value: PAYMENT_OPTIONS?.ONE_TIME, label: 'One Time' },
                  {
                    value: PAYMENT_OPTIONS?.INSTALLMENTS,
                    label: 'Installments',
                  },
                ],
                disabled: isTracking,
              })}
            </Grid>
            {isMotorInsurance(expiredPolicyData?.insuranceType) && (
              <>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Insured Name',
                    name: 'insuredName',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Vehicle Number',
                    name: 'vehicleNumber',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
                <Grid item xs={3}>
                  {renderTextField({
                    label: 'Vehicle Make & Model',
                    name: 'vehicleMakeModel',
                    required: true,
                    disabled: isTracking,
                  })}
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth className={classes.fieldContainer}>
                <div className={classes.formLabel}>
                  <span>
                    Coverages <span className={classes.asterisk}></span>
                  </span>
                </div>
                <Autocomplete
                  options={formik?.values?.coverages}
                  multiple
                  value={formik?.values?.coverages}
                  className={classes.coveragesField}
                  disabled={isTracking}
                  renderTags={(value, getTagProps) =>
                    value?.map((option, index) => (
                      <Chip
                        key={`${option}${index}`}
                        label={option}
                        {...getTagProps({ index })}
                        onDelete={() => handleDeleteCoverage(formik, index)}
                      />
                    ))
                  }
                  id='coverages'
                  size='small'
                  onChange={(value) =>
                    formik?.setFieldValue('coverages', value)
                  }
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      size='small'
                      fullWidth
                      className={classes.coveragesTextField}
                      name='coverages'
                      value={
                        !formik?.values?.coverages
                          ? 'Please enter *'
                          : formik?.values?.coverages.join(', ')
                      }
                      onChange={(e) => setEnteredCoverages(e?.target?.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          handleAddCoverage(
                            formik,
                            enteredCoverage,
                            setEnteredCoverages,
                            dispatch
                          )
                        }
                      }}
                      helperText={
                        formik?.touched?.coverages && formik?.errors?.coverages
                          ? formik?.errors?.coverages
                          : // prettier-ignore
                            'Don\'t use comma'
                      }
                      error={
                        formik?.touched?.coverages &&
                        !!formik?.errors?.coverages
                      }
                    />
                  )}
                />
                <div id='coverages' className={classes.description}>
                  Capture upto 5 coverages press enter to seperate
                </div>
              </FormControl>
            </Grid>
            {isEBPolicy(expiredPolicyData?.insuranceType) && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth className={classes.fieldContainer}>
                  <div className={classes.formLabel}>
                    <span>Individual Coverages</span>
                  </div>
                  <Autocomplete
                    options={formik?.values?.individualCoverages}
                    multiple
                    value={formik?.values?.individualCoverages}
                    className={classes.coveragesField}
                    disabled={isTracking}
                    renderTags={(value, getTagProps) =>
                      value?.map((option, index) => (
                        <Chip
                          key={index}
                          label={option}
                          {...getTagProps({ index })}
                          onDelete={() => {
                            const newCoverages = [
                              ...formik?.values?.individualCoverages,
                            ]
                            newCoverages?.splice(index, 1)
                            formik?.setFieldValue(
                              'individualCoverages',
                              newCoverages
                            )
                          }}
                        />
                      ))
                    }
                    id='individualCoverages'
                    size='small'
                    onChange={(event, value) =>
                      formik?.setFieldValue('individualCoverages', value)
                    }
                    disableCloseOnSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        size='small'
                        fullWidth
                        className={classes.coveragesTextField}
                        name='individualCoverages'
                        value={
                          !formik?.values?.individualCoverages
                            ? 'Please enter *'
                            : formik?.values?.individualCoverages?.join(', ')
                        }
                        onChange={async (e) => {
                          setEnteredCoverages(e?.target?.value)
                        }}
                        onKeyDown={async (ev) => {
                          if (ev.key === 'Enter') {
                            // ev.preventDefault();
                            await formik?.setFieldValue('individualCoverages', [
                              ...formik?.values?.individualCoverages,
                              enteredCoverage,
                            ])
                          }
                        }}
                        disabled={isTracking}
                        helperText={
                          formik?.touched?.individualCoverages &&
                          formik?.errors?.individualCoverages
                            ? formik?.errors?.individualCoverages
                            : // prettier-ignore
                              'Don\'t use comma'
                        }
                        error={
                          formik?.touched?.individualCoverages &&
                          !!formik?.errors?.individualCoverages
                        }
                      />
                    )}
                  />
                  <div className={classes.description}>
                    Capture upto 5 coverages press enter to separate
                  </div>
                </FormControl>
              </Grid>
            )}
          </Grid>
          {formik?.values?.paymentType === PAYMENT_OPTIONS?.INSTALLMENTS && (
            <InstallmentCalculator
              premium={formik?.values?.premiumPaid}
              policyStartDate={formik?.values?.policyStartDate}
              prefillData={formik?.values?.installmentDetails}
              onInstallmentsCalculated={handleInstallments}
              error={formik.errors}
              touched={formik.touched}
              disabled={isTracking}
            />
          )}

          <Grid
            container
            spacing={2}
            justifyContent='left'
            alignItems='center'
            className={classes.saveAsDraftButton}
          >
            {/* <Grid item>
              <Button
                type='button'
                id='saveButton'
                className={classes.btnStyles}
                disabled={isTracking}
                onClick={() => handleSaveAsDraft(formik.values)}
              >
                Save as Draft
              </Button>
            </Grid> */}

            {clientType === CLIENT_TYPE?.CORPORATE ? (
              <Grid item xs={3}>
                {
                  <FormControl fullWidth className={classes.fieldContainer}>
                    <TextField
                      select
                      variant='outlined'
                      name='empMigration'
                      placeholder='Emp migration'
                      size='small'
                      required
                      disabled={isTracking}
                      className={classes.formField}
                      value={formik?.values?.empMigration}
                      onChange={formik?.handleChange}
                      onBlur={formik?.handleBlur}
                      helperText={
                        formik?.touched?.empMigration &&
                        formik?.errors?.empMigration
                      }
                      error={
                        formik?.touched?.empMigration &&
                        !!formik?.errors?.empMigration
                      }
                    >
                      <MenuItem value={RENEWAL_MIGRATION?.MIGRATE_EMPLOYEES}>
                        Migrate Employees
                      </MenuItem>
                      <MenuItem
                        value={RENEWAL_MIGRATION?.DONOT_MIGRATE_EMPLOYEES}
                      >
                        Don&apos;t Migrate Employees
                      </MenuItem>
                    </TextField>
                  </FormControl>
                }
              </Grid>
            ) : (
              <Grid item xs={3}>
                {
                  <FormControl fullWidth className={classes.fieldContainer}>
                    <TextField
                      select
                      variant='outlined'
                      name='empMigration'
                      placeholder='Emp migration'
                      size='small'
                      required
                      disabled={isTracking}
                      className={classes.formField}
                      value={formik?.values?.empMigration}
                      onChange={formik?.handleChange}
                      onBlur={formik?.handleBlur}
                      helperText={
                        formik?.touched?.empMigration &&
                        formik?.errors?.empMigration
                      }
                      error={
                        formik?.touched?.empMigration &&
                        !!formik?.errors?.empMigration
                      }
                    >
                      <MenuItem
                        value={RENEWAL_MIGRATION?.MIGRATE_FAMILY_MEMBERS}
                      >
                        Migrate Family Members
                      </MenuItem>
                      <MenuItem
                        value={RENEWAL_MIGRATION?.DONOT_MIGRATE_FAMILY_MEMBERS}
                      >
                        Don&apos;t Migrate Family Members
                      </MenuItem>
                    </TextField>
                  </FormControl>
                }
              </Grid>
            )}
            <Grid item>
              <Button
                id='cancelButton'
                className={classes.btnStyles}
                onClick={closeModal}
              >
                close
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </>
  )
}

export default TrackFormFields

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: '20px 20px 60px 20px',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  formField: {
    width: '100%',
    fontSize: '8px',
  },
  formLabel: {
    display: 'flex',
    fontSize: '14px',
  },
  uploadLogoButton: {
    width: '100%',
    justifyContent: 'space-between',
    border: '1.5px dashed grey',
  },
  inputFileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
    cursor: 'pointer',
    maxWidth: '200px',
    display: 'inline-block',
  },
  filesContainer: {
    width: '100%',
    margin: '10px',
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    overflowX: 'scroll',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px',
    },
  },
  uploadedFile: {
    minWidth: '100%',
    border: '1px solid green',
    borderRadius: '5px',
    padding: '0.4rem',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
  },
  uploadedFileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
  },
  uploadedDocumentDelete: {
    cursor: 'pointer',
    color: 'red',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  btnStyles: {
    borderRadius: '25px',
    textTransform: 'capitalize',
    background: COLORS.DARK_PINK,
    color: COLORS.WHITE,
    padding: '8px 25px',
    fontSize: '14px',
    lineHeight: 'unset !important',
    '&:hover': {
      backgroundColor: COLORS.DARK_PINK,
    },
  },
  description: {
    color: 'gray',
  },
  coveragesField: {
    '& .MuiAutocomplete-popupIndicator': {
      padding: 0,
    },
    '&.css-4xbbh5-MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
      {
        padding: 0,
      },
  },
  selectField: {
    width: '100%',
    height: '40px !important',
    fontSize: '14px !important',
    '&  .MuiOutlinedInput-input': {
      height: '0.7rem',
    },
  },
  datePicker: {
    '&  .MuiOutlinedInput-root': {
      height: '2.5rem !important',
    },
  },
  autoCompleteContainer: {
    '&.css-l3ln04-MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
      {
        padding: 'unset',
      },
  },
  autoCompleteTextfield: {
    '&.MuiAutocomplete-popupIndicator': {
      padding: 0,
    },
  },
  errorText: {
    fontSize: '0.8rem',
    marginTop: '5px',
    color: 'red',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    // gap:'5px',
  },
  intermediary: {
    margin: theme.spacing(1),
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  customIntermediary: {
    margin: theme.spacing(1),
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  asterisk: {
    color: 'red',
  },
  formTextContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  formText: {
    width: '80%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  headingText: {
    marginBottom: '20px',
  },
  renewalFilesUpload: {
    width: '30%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  UploadFile: {
    display: 'none',
  },
  fileDownload: {
    padding: 0,
  },
  saveAsDraftButton: {
    marginTop: '20px',
  },
}))
